import dayjs from "dayjs";
import { type DocumentVersionSummary } from "~/api/types";

export const FilterOperatorEnum = {
  gte: "gte",
  lte: "lte",
  eq: "eq",
  in: "in",
  isNull: "isNull",
} as const;

export type FilterOperator =
  (typeof FilterOperatorEnum)[keyof typeof FilterOperatorEnum];

export const filterFields = [
  "publishedAt",
  "adoptedAt",
  "status",
  "tier",
  "category",
  "tag",
  "owner",
  "publisher",
  "reviewFrom",
  "reviewTo",
] as const;

export type FilterField = (typeof filterFields)[number];

export const dateFilterKeys = [
  "publishedAt",
  "adoptedAt",
  "reviewFrom",
  "reviewTo",
] as const;

export type DateFilterKey = (typeof dateFilterKeys)[number];

export const nullableDateFilterKeys = [
  "adoptedAt",
  "reviewFrom",
  "reviewTo",
] as const;

export type NullableDateFilterKey = (typeof nullableDateFilterKeys)[number];

export const isNullableDateFilterKey = (
  field: FilterField,
): field is NullableDateFilterKey =>
  (nullableDateFilterKeys as readonly string[]).includes(field);

export const multiSelectFilterKeys = [
  "status",
  "tier",
  "category",
  "tag",
  "owner",
  "publisher",
] as const;

export type MultiSelectFilterKey = (typeof multiSelectFilterKeys)[number];

export const filterIsDateByField = (
  field: FilterField,
): field is DateFilterKey =>
  (dateFilterKeys as readonly string[]).includes(field);

export type FilterCondition = {
  field: FilterField;
  operator: FilterOperator;
  value: string | string[];
};

export const getFilterLabels = (
  t: (key: string, options?: Record<string, unknown>) => string,
): Record<FilterField, string> => ({
  publishedAt: t("commons.document.publishedAt"),
  adoptedAt: t("commons.document.adoptedAt"),
  status: t("commons.document.status"),
  tier: t("commons.document.tier"),
  category: t("commons.document.category"),
  tag: t("commons.document.tags"),
  owner: t("commons.document.owner"),
  publisher: t("commons.document.publisher"),
  reviewFrom: t("commons.document.reviewFrom"),
  reviewTo: t("commons.document.reviewTo"),
});

export const toDateKey = (value: string | Date) => {
  const date = dayjs(value);
  if (!date.isValid()) return undefined;
  return date.format("YYYY-MM-DD");
};

const dateParamKeys: Record<DateFilterKey, { after: string; before: string }> =
  {
    publishedAt: { after: "publishedAfter", before: "publishedBefore" },
    adoptedAt: { after: "adoptedAfter", before: "adoptedBefore" },
    reviewFrom: { after: "reviewFromAfter", before: "reviewFromBefore" },
    reviewTo: { after: "reviewToAfter", before: "reviewToBefore" },
  };

const evaluateFilter = (
  rowValue: string,
  filter: Pick<FilterCondition, "operator" | "value">,
): boolean | undefined => {
  if (filter.operator === "in") {
    if (!Array.isArray(filter.value)) {
      return undefined;
    }
    return filter.value.includes(rowValue);
  }

  if (typeof filter.value !== "string") {
    return undefined;
  }

  if (filter.operator === "gte") {
    return rowValue >= filter.value;
  }
  if (filter.operator === "lte") {
    return rowValue <= filter.value;
  }

  return rowValue === filter.value;
};

export const parseFilters = (
  searchParams: URLSearchParams,
): FilterCondition[] => {
  const filters: FilterCondition[] = [];

  for (const field of multiSelectFilterKeys) {
    const values = searchParams.getAll(field);
    if (values.length === 0) continue;

    filters.push(
      values.length === 1
        ? { field, operator: FilterOperatorEnum.eq, value: values[0]! }
        : { field, operator: FilterOperatorEnum.in, value: values },
    );
  }

  for (const field of dateFilterKeys) {
    const paramKeys = dateParamKeys[field];

    const afterValue = searchParams.get(paramKeys.after);
    if (afterValue && toDateKey(afterValue) !== undefined) {
      filters.push({
        field,
        operator: FilterOperatorEnum.gte,
        value: afterValue,
      });
    }

    const beforeValue = searchParams.get(paramKeys.before);
    if (beforeValue && toDateKey(beforeValue) !== undefined) {
      filters.push({
        field,
        operator: FilterOperatorEnum.lte,
        value: beforeValue,
      });
    }
  }

  for (const field of nullableDateFilterKeys) {
    if (searchParams.get(field) === "null") {
      filters.push({
        field,
        operator: FilterOperatorEnum.isNull,
        value: "null",
      });
    }
  }

  return filters;
};

export const buildFilterParamKeysForField = (field: FilterField): string[] => {
  if (filterIsDateByField(field)) {
    const paramKeys = dateParamKeys[field];
    const keys = [paramKeys.after, paramKeys.before];
    if (isNullableDateFilterKey(field)) {
      keys.push(field);
    }
    return keys;
  }
  return [field];
};

export const buildSearchParamValuesFromFilters = (
  filters: FilterCondition[],
): Record<string, string | string[] | undefined> => {
  const clearedParams = filterFields.reduce<Record<string, undefined>>(
    (acc, field) => {
      buildFilterParamKeysForField(field).forEach((paramKey) => {
        acc[paramKey] = undefined;
      });
      return acc;
    },
    {},
  );

  return filters.reduce<Record<string, string | string[] | undefined>>(
    (acc, filter) => {
      if (filter.operator === FilterOperatorEnum.isNull) {
        acc[filter.field] = "null";
      } else if (filterIsDateByField(filter.field)) {
        const paramKeys = dateParamKeys[filter.field];
        const paramKey =
          filter.operator === FilterOperatorEnum.gte
            ? paramKeys.after
            : paramKeys.before;
        acc[paramKey] =
          typeof filter.value === "string" ? filter.value : undefined;
      } else {
        acc[filter.field] = Array.isArray(filter.value)
          ? filter.value
          : [filter.value];
      }
      return acc;
    },
    { ...clearedParams },
  );
};

export const buildSearchParams = (
  values: Record<string, string | string[] | undefined>,
): URLSearchParams => {
  const searchParams = new URLSearchParams();

  Object.entries(values).forEach(([key, value]) => {
    if (value === undefined) return;
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v.length > 0) searchParams.append(key, v);
      });
    } else if (value.length > 0) {
      searchParams.set(key, value);
    }
  });

  return searchParams;
};

export const isFilterFieldValueValid = (field: FilterField, value: string) => {
  if (value === "null" && isNullableDateFilterKey(field)) {
    return true;
  }
  if (filterIsDateByField(field)) {
    return toDateKey(value) !== undefined;
  }
  return value.trim().length > 0;
};

const getDocumentVersionFilterValue = (
  documentVersion: DocumentVersionSummary,
  field: FilterField,
): string | string[] | null => {
  switch (field) {
    case "publishedAt":
      return documentVersion.publishedAt;
    case "adoptedAt":
      return documentVersion.adoptedAt;
    case "status":
      return documentVersion.status;
    case "tier":
      return documentVersion.document.tier.id.toString();
    case "category":
      return documentVersion.document.category.id.toString();
    case "tag":
      return documentVersion.document.tagIds.map((tagId) => tagId.toString());
    case "owner":
      return documentVersion.document.owner.userId.toString();
    case "publisher":
      return documentVersion.document.publisher?.userId.toString() ?? null;
    case "reviewFrom":
      return documentVersion.reviewFrom;
    case "reviewTo":
      return documentVersion.reviewTo;
  }
};

const matchesDocumentVersionFilter = (
  documentVersion: DocumentVersionSummary,
  filter: FilterCondition,
) => {
  const fieldValue = getDocumentVersionFilterValue(
    documentVersion,
    filter.field,
  );

  if (filter.field === "tag") {
    if (!Array.isArray(fieldValue)) {
      return false;
    }

    if (filter.operator === "eq" && typeof filter.value === "string") {
      return fieldValue.includes(filter.value);
    }

    if (filter.operator === "in" && Array.isArray(filter.value)) {
      return fieldValue.some((value) => filter.value.includes(value));
    }

    return true;
  }

  if (filter.operator === FilterOperatorEnum.isNull) {
    return fieldValue === null;
  }

  if (filterIsDateByField(filter.field)) {
    if (typeof fieldValue !== "string" || typeof filter.value !== "string") {
      return false;
    }

    const dateValue = toDateKey(fieldValue);
    if (!dateValue) {
      return false;
    }

    const result = evaluateFilter(dateValue, {
      operator: filter.operator,
      value: filter.value,
    });
    return result ?? true;
  }

  if (typeof fieldValue !== "string") {
    return false;
  }

  const result = evaluateFilter(fieldValue, {
    operator: filter.operator,
    value: filter.value,
  });
  return result ?? true;
};

export const filterDocumentVersions = (
  documentVersions: DocumentVersionSummary[],
  filters: FilterCondition[],
) =>
  documentVersions.filter((documentVersion) =>
    filters.every((filter) =>
      matchesDocumentVersionFilter(documentVersion, filter),
    ),
  );
